import { Button, Checkbox, Form, Table } from "antd";
import { useEffect, useState } from "react";
import { SaveOutlined } from '@ant-design/icons';
import { IEquipmentDocumentType, IEquipmentDocumentTypeProps, IEquipmentDocumentTypeResult, IEquipmentDocumentTypeStates } from "./interfaces";
import { Lookup } from "../../components/lookup";
import { enmControlType, enmPropertyType } from "../../components/entityManager/interfaces";
import { Core } from "../../components/infrastructure/core";
import { useContext } from "react";
import AppContext from "../../components/infrastructure/appContext";
import { enmEntityState, enmValidationLevel, ICallOptions, IResult, IResultSet } from "../../components/infrastructure/models";
import { Redirect } from "react-router-dom";
import { IDamageReason } from "../damageReason/interfaces";
import { IDocumentType } from "../documentType/interfaces";
import { DamageReason } from "../damageReason";
import './index.css';

type LayoutType = Parameters<typeof Form>[0]['layout'];

const EquipmentDocumentType = (props: IEquipmentDocumentTypeProps) => {
    const context = useContext(AppContext);
    const [loading, setLoading] = useState(true);
    const [state, setState] = useState<IEquipmentDocumentTypeStates>({
        loadData: true,
        isAuthorized: true,
        selectedItems: [],
        documentTypes: [],
        equipmentDocumentTypes: [],
        saveAccess: false,
        currentEquipmentId: props.params().equipment?.Id
    });
    const { loadData } = state;
    const [form] = Form.useForm();
    const [formLayout] = useState<LayoutType>('horizontal');
    const formItemLayout = {
        labelCol: { span: 4 },
        wrapperCol: { span: 18 },
    };

    useEffect(() => {
        if (props.params().equipment.Id !== state.currentEquipmentId) {
            setState({
                loadData: true,
                isAuthorized: true,
                selectedItems: [],
                documentTypes: [],
                equipmentDocumentTypes: [],
                saveAccess: false,
                currentDamageReasonId: state.currentDamageReasonId,
                currentEquipmentId: props.params().equipment.Id
            });
            setLoading(true);
        }
    }, [props]);

    if (!state.isAuthorized) {
        let loginUrl = '/login';
        if (context?.path)
            loginUrl += '?' + encodeURIComponent(context.path);
        return (<Redirect to={loginUrl} />);
    }

    if (loadData) {
        let res: IResultSet<IEquipmentDocumentTypeResult> | null = null;

        let opt: ICallOptions = {
            url: "equipmentdocumenttype/getequipments",
            data: { equipmentId: props.params().equipment.Id, damageReasonId: state.currentDamageReasonId },
            onSuccess: (result) => {
                res = result as any;
            },
            onComplete: () => {
                if (res && res.Data) {
                    if (!res.ValidationState.Successful) {
                        let isAuthorized = true;
                        if (res.ValidationState.ValidationResults.find(v => v.Level === enmValidationLevel.AccessDenied || v.Level === enmValidationLevel.Unauthorized)) {
                            Core.errorNotification(res.ValidationState.ValidationResults[0].Message!);
                            isAuthorized = false;
                        }
                        setState({
                            loadData: false,
                            isAuthorized: isAuthorized,
                            selectedItems: [],
                            documentTypes: [],
                            equipmentDocumentTypes: [],
                            saveAccess: false
                        });
                        setLoading(false);
                        return;
                    }

                    let selectedItems = res.Data.EquipmentDocumentTypes.map(e => {
                        return res!.Data!.DocumentTypes.find(d => d.Id === e.DocumentTypeId)?.RowGuid!;
                    });

                    const equipmentDocumentTypes = res.Data.EquipmentDocumentTypes;

                    setState({
                        loadData: false,
                        isAuthorized: true,
                        documentTypes: res.Data.DocumentTypes.map(d => { return { ...d, Required: equipmentDocumentTypes.find(e => e.DocumentTypeId === d.Id)?.Required }; }),
                        selectedItems: selectedItems,
                        equipmentDocumentTypes: equipmentDocumentTypes,
                        saveAccess: res.Data.SaveAccess,
                        currentDamageReasonId: (form.getFieldValue("DamageReason") as IDamageReason)?.Id,
                        currentEquipmentId: props.params().equipment?.Id
                    });
                    setLoading(false);
                    return;
                }
                setState({
                    loadData: false,
                    isAuthorized: true,
                    documentTypes: [],
                    selectedItems: [],
                    equipmentDocumentTypes: [],
                    saveAccess: false,
                    currentEquipmentId: props.params().equipment?.Id
                });
                setLoading(false);
            }
        };
        Core.callApi(opt);
    }

    const save = (changes: IEquipmentDocumentType[]) => {
        if (!changes.length) {
            Core.warningNotification("لطفا ابتدا اطلاعات را تغییر دهید");
            return;
        }
        setLoading(true);
        let res: IResult | null = null;

        let opt: ICallOptions = {
            url: "equipmentdocumenttype/save",
            data: { entities: changes },
            onSuccess: (result) => {
                res = result;
            },
            onComplete: () => {
                if (res && res) {
                    if (!res.ValidationState.Successful) {
                        let isAuthorized = true;
                        if (res.ValidationState.ValidationResults.find(v => v.Level === enmValidationLevel.AccessDenied || v.Level === enmValidationLevel.Unauthorized)) {
                            Core.errorNotification(res.ValidationState.ValidationResults[0].Message!);
                            isAuthorized = false;
                        }
                        setState({
                            loadData: false,
                            isAuthorized: isAuthorized,
                            selectedItems: [],
                            documentTypes: [],
                            equipmentDocumentTypes: [],
                            saveAccess: false
                        });
                        setLoading(false);
                        return;
                    }
                    Core.successNotification("اطلاعات با موفقیت ذخیره شد");

                    setState({
                        loadData: true,
                        isAuthorized: true,
                        selectedItems: [],
                        documentTypes: [],
                        equipmentDocumentTypes: [],
                        saveAccess: false,
                        currentDamageReasonId: state.currentDamageReasonId,
                        currentEquipmentId: state.currentEquipmentId
                    });
                    setLoading(true);
                    return;
                }
                setState({
                    loadData: false,
                    isAuthorized: true,
                    documentTypes: [],
                    selectedItems: [],
                    equipmentDocumentTypes: [],
                    saveAccess: false,
                    currentEquipmentId: state.currentEquipmentId
                });
                setLoading(false);
            }
        };
        Core.callApi(opt);
    };

    return (
        <Form
            {...formItemLayout}
            layout={formLayout}
            form={form}
            initialValues={{ layout: formLayout }}
            onFinish={() => {
                let changes: IEquipmentDocumentType[] = [];
                state.selectedItems.forEach(item => {
                    let documentType = state.documentTypes.find(d => d.RowGuid === item);

                    if (documentType) {
                        let documentTypeId = documentType.Id;
                        const equipmentDocumentType = state.equipmentDocumentTypes.find(r => r.DocumentTypeId === documentTypeId);
                        if (!equipmentDocumentType)
                            changes.push({ RowGuid: Core.newId(), DamageReasonId: state.currentDamageReasonId!, DocumentTypeId: documentTypeId, EquipmentId: state.currentEquipmentId!, State: enmEntityState.Added, Required: (documentType as any).Required } as any);
                        else if ((documentType as any).Required !== equipmentDocumentType?.Required) {
                            changes.push({ Id: equipmentDocumentType.Id, RowGuid: equipmentDocumentType.RowGuid, DamageReasonId: equipmentDocumentType.DamageReasonId, DocumentTypeId: equipmentDocumentType.DocumentTypeId, EquipmentId: equipmentDocumentType.EquipmentId, State: enmEntityState.Modified, Required: (documentType as any).Required, ModifiedProperties: ['Required'], RowVrsn: equipmentDocumentType.RowVrsn } as any);
                        }
                    }
                });
                state.equipmentDocumentTypes.forEach(item => {
                    let documentType = state.documentTypes.find(d => d.Id === item.DocumentTypeId);
                    if (documentType && !state.selectedItems.find(r => r === documentType?.RowGuid)) {
                        changes.push({ Id: item.Id, RowGuid: item.RowGuid, RowVrsn: item.RowVrsn, State: enmEntityState.Deleted } as any);
                    }
                });

                save(changes);
            }}
            style={{ margin: 10 }}
        >
            <Form.Item
                label={"دلیل خسارت"}
                name={"DamageReason"}
                rules={[{ required: true, message: "لطفا دلیل خسارت را انتخاب نمایید" }]}
                key={"DamageReason"}
                style={{ margin: 10 }}
            >
                <Lookup
                    onChange={(value?: IDamageReason) => {
                        setState({
                            loadData: true,
                            isAuthorized: true,
                            documentTypes: [],
                            selectedItems: [],
                            equipmentDocumentTypes: [],
                            saveAccess: false,
                            currentDamageReasonId: value?.Id,
                            currentEquipmentId: props.params().equipment?.Id
                        });
                        setLoading(true);
                    }}
                    isLookupMode={true}
                    property={{
                        controlType: enmControlType.lookup,
                        propertyType: enmPropertyType.number,
                        dataIndex: "DamageReason",
                        title: "دلیل خسارت",
                        lookup: {
                            lookupComponent: DamageReason,
                            lookupProperty: "DamageReason",
                            lookupDisplayFields: ["Name"]
                        }
                    }}
                    parent={props.parent}
                    key={"DamageReason"}
                />
            </Form.Item>

            <Form.Item
                label={"نوع مستند"}
                name={"DocumnetType"}
                key={"DocumnetType"}
                style={{ margin: 10 }}
            >
                <Table
                    rowKey="RowGuid"
                    onHeaderRow={() => {
                        return {
                            className: 'ant-table-thead-checkbox',
                        };
                    }}
                    rowSelection={{
                        type: 'checkbox',
                        selectedRowKeys: state.selectedItems,
                        columnWidth: "50px",
                        onChange: (selectedRowKeys: React.Key[], _: IDocumentType[]) => {
                            setState({
                                loadData: false,
                                isAuthorized: true,
                                documentTypes: state.documentTypes,
                                selectedItems: selectedRowKeys,
                                equipmentDocumentTypes: state.equipmentDocumentTypes,
                                saveAccess: state.saveAccess,
                                currentDamageReasonId: state.currentDamageReasonId,
                                currentEquipmentId: state.currentEquipmentId
                            });
                            setLoading(false);
                        }
                    }}
                    pagination={{
                        style: { marginLeft: 10 }
                    }}
                    dataSource={state.documentTypes}
                    className="box"
                    columns={[
                        {
                            dataIndex: "Name",
                            title: "نوع مستند"
                        },
                        {
                            dataIndex: "Required",
                            title: "اجباری؟",
                            render: (_, record) => {
                                const equipmentDocumentType = state.equipmentDocumentTypes.find(d => d.DocumentTypeId === record.Id);
                                const { documentTypes } = state;
                                return (
                                    <Checkbox defaultChecked={equipmentDocumentType?.Required} onChange={(e) => {
                                        const documentType = documentTypes.find(d => d.Id === record.Id) as any;
                                        if (documentType)
                                            documentType.Required = e.target.checked;
                                        setState({ ...state, documentTypes });
                                    }} />
                                );
                            }
                        }
                    ]}
                    loading={loading}
                />
            </Form.Item>

            <Form.Item wrapperCol={{ dir: 'ltr' }} style={{ borderTop: '1px solid', borderColor: '#e5e5e5', marginLeft: 20 }}>
                <Button disabled={!state.saveAccess} type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading} style={{ lineHeight: '0.5em', verticalAlign: 'middle', width: "100px", marginTop: 10 }}>
                    ذخیره
                </Button>
            </Form.Item>
        </Form>
    );
}

export default EquipmentDocumentType;